/*
 * Copyright (c) 2026.
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden volgens de voorwaarden die zijn vastgelegd in een bijbehorende schriftelijke overeenkomst met Spin in het Web B.V.
 */

/**
 * Auteur: Jelmer Jellema - Spin in het Web B.V.
 * Gegenereerd met Junie (AI), 24-06-2026
 *
 * Knop links naast een node om die node te verwijderen. Vervangt de qtip-
 * gebaseerde `createDeletebtn` uit `cytocanvas.modelview.controller.js`.
 *
 * De prullenbak is zichtbaar wanneer de node geselecteerd is én de "baas"
 * (`DlModelCanvasComponent`) zegt dat de node verwijderd mag worden. De
 * mag-deleten-check en de verwijderactie zelf lopen via het canvas, dat de
 * verwijderactie aan de samenwerk-baas geeft.
 */
import {ChangeDetectionStrategy, Component, inject} from '@angular/core';
import {TranslateModule, TranslatePipe} from '@ngx-translate/core';
import {DlNodeVolgerDirective} from '../../../directives/dl-node-volger/dl-node-volger.directive';
import {DlModelCanvasComponent} from '../../canvas/dl-modelcanvas/dl-modelcanvas.component';
import {BaseNodeGekoppeldComponent} from '../base-node-gekoppeld.component';

@Component({
    selector: 'dl-node-prullenbak',
    standalone: true,
    imports: [DlNodeVolgerDirective, TranslateModule, TranslatePipe],
    templateUrl: './dl-node-prullenbak.component.html',
    styleUrls: ['./dl-node-prullenbak.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DlNodePrullenbakComponent extends BaseNodeGekoppeldComponent {

    constructor() {
        super();
    }

    /**
     * Pixel-offset om de knop iets links van de node te plaatsen (analoog aan
     * de oude qtip `adjust: { x: 5 }`-instelling).
     */
    protected readonly offset = {x: -5, y: 0};

    /**
     * Het canvas waarbinnen deze prullenbak leeft. Levert globale state en
     * de model-acties `magNodeDeleten` en `deleteNode`. Geïnjecteerd als
     * host-ancestor; geen extra input nodig.
     */
    private readonly modelcanvas = inject(DlModelCanvasComponent, {host: true});

    /**
     * Bepaalt of de prullenbak op dit moment getoond moet worden. Combineert
     * de selectie-state met de mag-verwijderen-check van het canvas (die zowel
     * gewone nodes als value-/ineq-history-containers afhandelt). Weg zodra het naamveld van de
     * node is geopend; de prullenbak komt terug bij de volgende selectie. En weg bij een node die
     * tijdens het leggen van een relatie wordt aangeklikt als tweede argument.
     */
    protected get zichtbaar(): boolean {
        return this.geselecteerd() && !!this.node && !this.modelcanvas.isLabelBewerkt(this.node)
            && this.modelcanvas.magNodeMenuTonen(this.node) && this.modelcanvas.magNodeVerwijderen(this.node);
    }

    /**
     * Klikhandler op de prullenbak: vraagt het canvas om de node te verwijderen
     * (of, voor een history-container, te verbergen).
     */
    protected klikVerwijder(event: MouseEvent): void {
        event.stopPropagation();
        this.modelcanvas.verwijderNode(this.node);
    }
}
